import React from 'react';

const phaseText = {
  'await-roll': 'Würfeln',
  public: 'Gemeinsame Aktion',
  colored: 'Farbaktion',
  finished: 'Spiel beendet'
};

export function ActionPanel({ state, viewerPlayer, remainingSeconds, timerPaused, busy, isRolling, onRoll, onAction, childHelpActive = false, recommendPass = false }) {
  const active = state.players.find((player) => player.id === state.activePlayerId);
  const submitted = viewerPlayer?.submittedPublic;
  const waiting = state.players.filter((player) => !player.submittedPublic).length;

  if (state.status === 'finished') {
    return (
      <section className="action-panel action-panel--finished">
        <span className="eyebrow">{phaseText.finished}</span>
        <h2>{state.ranking?.filter((item) => item.rank === 1).map((item) => item.name).join(' & ')} gewinnt!</h2>
        <p>Die endgültige Rangliste findest du unter dem Wertungsblatt.</p>
        {state.viewer.isHost && <button className="button button--primary" disabled={busy} onClick={() => onAction('game:restart')}>Neue Runde</button>}
      </section>
    );
  }

  if (isRolling) {
    return (
      <section className="action-panel action-panel--rolling" aria-live="polite">
        <div className="action-panel__status">
          <span className="eyebrow">Zug {state.turnNumber}</span>
          <strong>{active?.name} würfelt …</strong>
          <small>Das serverseitige Ergebnis wird gleich freigegeben.</small>
        </div>
        <div className="roll-status" aria-hidden="true"><i /><i /><i /></div>
        <p className="waiting-text">Die Würfel landen gleich.</p>
      </section>
    );
  }

  if (state.phase === 'await-roll') {
    return (
      <section className="action-panel">
        <div className="action-panel__status"><span className="eyebrow">Zug {state.turnNumber}</span><strong>{active?.name} ist am Zug</strong></div>
        {state.viewer.isActive && state.viewer.role === 'player' ? (
          <button className={`button button--primary button--roll ${childHelpActive ? 'is-child-target' : ''}`} disabled={busy} onClick={onRoll}><span aria-hidden="true">{childHelpActive ? '👉 🎲' : '🎲'}</span> Würfeln</button>
        ) : (
          <p className="waiting-text">Warte auf den Wurf …</p>
        )}
        <Timer seconds={remainingSeconds} unlimited={state.settings.turnTimerSeconds === 0} paused={timerPaused} />
      </section>
    );
  }

  if (state.phase === 'public') {
    if (state.viewer.role !== 'player') {
      return <section className="action-panel"><span className="eyebrow">Gemeinsame Aktion</span><h2>Alle wählen zur weißen Summe {state.whiteTotal}</h2><p>{waiting} Auswahl(en) fehlen noch.</p><Timer seconds={remainingSeconds} unlimited={state.settings.turnTimerSeconds === 0} paused={timerPaused} /></section>;
    }
    return (
      <section className="action-panel">
        <div className="action-panel__status">
          <span className="eyebrow">1. Gemeinsame Aktion</span>
          <strong>Weiße Summe: {state.whiteTotal}</strong>
          <small>
            {state.viewer.isActive
              ? `Kreuze die ${state.whiteTotal} an oder lasse Weiß aus. Danach darfst du zusätzlich eine Farbkombination wählen.`
              : `Kreuze die ${state.whiteTotal} in einer Reihe an oder lasse die weiße Aktion aus.`}
          </small>
        </div>
        {submitted ? (
          <div className="choice-confirmed">✓ Auswahl gespeichert · noch {waiting} offen</div>
        ) : (
          <>
            <span className="hint-arrow">Tippe auf ein leuchtendes Feld im Wertungsblatt.</span>
            <button className={`button button--ghost ${recommendPass ? 'is-child-target' : ''}`} disabled={busy} onClick={() => onAction('game:submitPublic', { move: null })}><span aria-hidden="true">{recommendPass ? '👉 ⏭️ ' : ''}</span>Weiße Aktion auslassen</button>
          </>
        )}
        <Timer seconds={remainingSeconds} unlimited={state.settings.turnTimerSeconds === 0} paused={timerPaused} />
      </section>
    );
  }

  if (state.phase === 'colored') {
    if (!state.viewer.isActive || state.viewer.role !== 'player') {
      return <section className="action-panel"><span className="eyebrow">2. Farbaktion</span><h2>{active?.name} darf zusätzlich ankreuzen</h2><p>Ein weißer Würfel plus ein Farbwürfel.</p><Timer seconds={remainingSeconds} unlimited={state.settings.turnTimerSeconds === 0} paused={timerPaused} /></section>;
    }
    return (
      <section className="action-panel">
        <div className="action-panel__status">
          <span className="eyebrow">2. Deine Farbaktion</span>
          <strong>Optional: zweites Kreuz</strong>
          <small>Markiere Weiß + Farbe oder beende deinen Zug jetzt ohne weiteres Kreuz.</small>
        </div>
        <span className="hint-arrow">{state.viewerMoves.colored.length ? 'Erlaubte Felder leuchten.' : 'Keine gültige Kombination verfügbar.'}</span>
        <button className={`button button--ghost ${recommendPass ? 'is-child-target' : ''}`} disabled={busy} onClick={() => onAction('game:submitColored', { move: null })}><span aria-hidden="true">{recommendPass ? '👉 ⏭️ ' : ''}</span>Zug beenden</button>
        <Timer seconds={remainingSeconds} unlimited={state.settings.turnTimerSeconds === 0} paused={timerPaused} />
      </section>
    );
  }

  return null;
}

function Timer({ seconds, unlimited = false, paused = false }) {
  const urgent = !paused && !unlimited && seconds <= 10;
  const title = paused ? 'Zugzeit pausiert' : unlimited ? 'Unbegrenzte Zugzeit' : 'Verbleibende Zeit';
  return <div className={`turn-timer ${urgent ? 'is-urgent' : ''} ${paused ? 'is-paused' : ''}`} title={title}><span style={{ '--progress': unlimited || paused ? '100%' : `${Math.max(0, Math.min(100, seconds / 1.2))}%` }} /><strong>{paused ? 'Ⅱ' : unlimited ? '∞' : `${seconds}s`}</strong></div>;
}
